import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSEO } from '../hooks/useSEO';

const AboutUs = () => {
  const [activeValue, setActiveValue] = useState(0);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  useSEO({
    title: "About AM CARDMATE - Our Story & Mission",
    description: "Learn about AM CARDMATE's mission to revolutionize professional networking with sustainable digital solutions.",
    image: "/assets/aboutus.jpg",
    keywords: "about us, company story, digital networking"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About AM CARDMATE",
        "description": "Learn about AM CARDMATE's mission to revolutionize professional networking",
        "url": "https://amcardmate.com/about"
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "AM CARDMATE",
        "founder": {
          "@type": "Person",
          "name": "Alina Macovei",
          "jobTitle": "Founder & CEO",
          "alumniOf": "Lancaster University",
          "sameAs": "https://www.linkedin.com/in/alina-macovei/"
        },
        "foundingDate": "2024-03",
        "description": "Digital business card platform with NFC technology",
        "numberOfEmployees": "1-10",
        "areaServed": "Worldwide",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "500" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const companyValues = [
    { icon: 'lightbulb', title: 'Innovation First', description: 'Constantly evolving our platform with cutting-edge features that redefine networking.', gradient: 'from-blue-400 to-blue-600', stat: 'Always', statLabel: 'Innovating' },
    { icon: 'rocket', title: 'Lightning Fast', description: 'Create and share your complete profile in seconds. Speed matters in networking.', gradient: 'from-purple-400 to-pink-600', stat: '3sec', statLabel: 'Card Creation' },
    { icon: 'shield-alt', title: 'Privacy Focused', description: 'Bank-level encryption with full control over what you share and with whom.', gradient: 'from-green-400 to-teal-600', stat: '100%', statLabel: 'Secure' },
    { icon: 'heart', title: 'User-Centered', description: 'Every feature is designed based on real user feedback and needs.', gradient: 'from-orange-400 to-red-600', stat: '24/7', statLabel: 'Support' }
  ];

  const platformFeatures = [
    { icon: 'mobile-alt', title: 'Native Apps', stat: 'iOS & Android', description: 'Beautiful native apps built with modern frameworks', gradient: 'from-blue-400 to-cyan-500' },
    { icon: 'chart-line', title: 'Smart Analytics', stat: 'Comprehensive', description: 'Track every interaction instantly', gradient: 'from-purple-400 to-pink-500' },
    { icon: 'magic', title: 'Instant Creation', stat: '3 Seconds', description: 'Create cards in just seconds', gradient: 'from-green-400 to-teal-500' },
    { icon: 'share-alt', title: 'Universal Share', stat: '5 Methods', description: 'Share via NFC, QR, link, and more', gradient: 'from-orange-400 to-red-500' },
    { icon: 'folder-open', title: 'Smart Collections', stat: 'Multiple', description: 'Organize contacts intelligently', gradient: 'from-indigo-400 to-purple-500' },
    { icon: 'wifi', title: 'NFC Writer', stat: 'Built-in', description: 'Program NFC tags directly', gradient: 'from-pink-400 to-rose-500' }
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "Marketing Director", company: "Tech Innovations Inc.", quote: "AM CARDMATE transformed how I network. The analytics feature alone has helped me follow up with the right connections at the right time.", rating: 5 },
    { name: "Michael Rodriguez", role: "CEO", company: "Growth Partners", quote: "Finally, a digital business card solution that actually works! The NFC feature is a game-changer for in-person events.", rating: 5 },
    { name: "Emily Thompson", role: "Sales Manager", company: "Global Solutions", quote: "I love how easy it is to update my information and have it instantly reflected everywhere. No more outdated business cards!", rating: 5 },
    { name: "David Park", role: "Real Estate Agent", company: "Park Properties", quote: "My clients are impressed by the professional look of my digital card. The QR code feature makes it so easy to share.", rating: 5 },
    { name: "Jessica Williams", role: "Startup Founder", company: "EcoTech Solutions", quote: "As a sustainability-focused company, going paperless with AM CARDMATE aligns perfectly with our values.", rating: 4 }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-blue-50/20 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-200/20 via-pink-200/20 to-transparent rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="sparkles" />
              <span>About AM CARDMATE</span>
            </div>
            
            <h1 className="text-h1 mb-6 text-dark">
              Building the Future of
              <span className="block text-gradient-blue-pink mt-2">Professional Networking</span>
            </h1>
            
            <p className="text-body-lg text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              One platform. Infinite possibilities. Zero paper waste. 
              We're revolutionizing how professionals connect and grow.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <motion.a href="#our-story" className="btn btn-primary group" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <FontAwesomeIcon icon="book-open" />
                <span>Read Our Story</span>
              </motion.a>
              
              <motion.button className="btn btn-outline group" onClick={() => setShowDemoPopup(true)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <FontAwesomeIcon icon="play-circle" />
                <span>Watch Demo</span>
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
                <motion.div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <FontAwesomeIcon icon="bullseye" className="text-white text-2xl" />
                  </div>
                  <h3 className="text-h4 text-dark mb-4">Our Mission</h3>
                  <p className="text-body text-gray-600 leading-relaxed">
                    To eliminate paper waste in professional networking while empowering individuals 
                    and businesses with smart, sustainable, and insightful digital solutions that 
                    create meaningful connections.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
                <motion.div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-purple-100 to-pink-200 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <FontAwesomeIcon icon="eye" className="text-white text-2xl" />
                  </div>
                  <h3 className="text-h4 text-dark mb-4">Our Vision</h3>
                  <p className="text-body text-gray-600 leading-relaxed">
                    A world where every professional connection is instant, insightful, and impactful. 
                    Where networking technology adapts to human needs, not the other way around.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Story */}
      <section id="our-story" className="py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20 relative overflow-hidden">
        <div className="absolute top-[20%] left-[10%] w-[500px] h-[500px] bg-gradient-to-br from-purple-200/20 via-blue-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="relative order-2 lg:order-1">
              <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-2xl p-8">
                <img src="/assets/image12.png" alt="Alina Macovei" className="w-full h-full object-cover rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                  <h3 className="text-h5 text-dark">Alina Macovei</h3>
                  <p className="text-body-sm text-gray-600">Founder & Developer</p>
                </div>
              </div>
              <motion.div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full opacity-20" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity }} />
            </div>
            
            <div className="relative order-1 lg:order-2">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="user" />
                <span>The Visionary</span>
              </div>
              
              <h2 className="text-h2 mb-6 text-dark">
                From Frustration to <span className="text-gradient-blue-pink">Innovation</span>
              </h2>
              
              <div className="space-y-4 text-body text-gray-600 leading-relaxed">
                <p>
                  "I was tired of collecting paper business cards that would end up in a drawer, 
                  never to be seen again. As a developer, I knew there had to be a better way."
                </p>
                
                <div className="border-l-4 border-primary-blue pl-6 py-4 my-6 bg-gradient-to-r from-primary-blue/5 to-accent-purple/5 rounded-r-xl">
                  <p className="italic text-gray-600">
                    "I envisioned a platform where sharing contact information would be instant, 
                    tracking connections would be automatic, and everything you need would be 
                    in one place - no external tools required."
                  </p>
                </div>
                
                <p>
                  That vision became AM CARDMATE - a comprehensive platform that handles everything 
                  from card creation to analytics, making professional networking smarter and 
                  more sustainable than ever before.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/20 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute bottom-[10%] right-[10%] w-[600px] h-[600px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <div className="badge badge-purple inline-flex mb-6">
                  <FontAwesomeIcon icon="microchip" />
                  <span>Technology</span>
                </div>
                
                <h2 className="text-h2 mb-6 text-dark">
                  Built with <span className="text-gradient-blue-pink">Modern Technology</span>
                </h2>
                
                <p className="text-body-lg text-gray-600 mb-8 leading-relaxed">
                  Our platform leverages cutting-edge technology to deliver a seamless experience 
                  across all devices, ensuring reliability, speed, and security at every touchpoint.
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: 'cloud', title: 'Cloud Infrastructure', desc: 'Scalable and reliable cloud architecture' },
                    { icon: 'lock', title: 'End-to-End Encryption', desc: 'Your data is protected at every level' },
                    { icon: 'sync', title: 'Real-Time Sync', desc: 'Instant updates across all platforms' },
                    { icon: 'mobile-alt', title: 'Native Performance', desc: 'Optimized Flutter apps for iOS and Android' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-md">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div>
                        <h4 className="text-body font-bold text-dark mb-1">{item.title}</h4>
                        <p className="text-body-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img src="/assets/image30.png" alt="Technology Stack" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-end p-8">
                    <div>
                      <h3 className="text-h4 mb-2 text-white drop-shadow-lg">Modern Tech Stack</h3>
                      <p className="text-body text-white/90 drop-shadow-md">Flutter for mobile, React for web, Node.js backend</p>
                    </div>
                  </div>
                </div>
                
                <motion.div className="absolute -top-4 -right-4 bg-white rounded-2xl px-4 py-2 shadow-xl z-20" animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity }}>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="check-circle" className="text-green-500" />
                    <span className="text-body font-semibold">99.9% Uptime</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="badge badge-pink inline-flex mb-4">
              <FontAwesomeIcon icon="heart" />
              <span>Our Values</span>
            </div>
            
            <h2 className="text-h2 mb-6 text-dark">
              What Drives Us <span className="text-gradient-blue-pink">Forward</span>
            </h2>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                {companyValues.map((value, index) => (
                  <div 
                    key={index}
                    className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                      activeValue === index 
                        ? 'bg-gradient-to-r from-primary-blue/10 to-accent-purple/10 shadow-lg scale-105' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveValue(index)}
                  >
                    <div className="flex items-center gap-4">
                      <motion.div 
                        className={`w-12 h-12 bg-gradient-to-br ${value.gradient} rounded-xl flex items-center justify-center text-white`}
                        animate={activeValue === index ? { rotate: 360 } : {}}
                        transition={{ duration: 0.6 }}
                      >
                        <FontAwesomeIcon icon={value.icon} className="text-xl" />
                      </motion.div>
                      <div className="flex-1">
                        <h3 className="text-body font-bold text-dark">{value.title}</h3>
                        <p className="text-body-sm text-gray-600">{value.description}</p>
                      </div>
                      <FontAwesomeIcon icon="chevron-right" className={`text-xl transition-all ${activeValue === index ? 'text-primary-blue translate-x-1' : 'text-gray-300'}`} />
                    </div>
                  </div>
                ))}
              </div>
              
              <motion.div 
                className={`bg-gradient-to-br ${companyValues[activeValue].gradient} rounded-3xl p-12 text-white relative overflow-hidden`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                key={activeValue}
              >
                <motion.div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full" animate={{ x: [0, 50, 0], y: [0, -50, 0] }} transition={{ duration: 10, repeat: Infinity }} />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <FontAwesomeIcon icon={companyValues[activeValue].icon} className="text-2xl" />
                  </div>
                  <h3 className="text-h4 mb-4">{companyValues[activeValue].title}</h3>
                  <div className="text-h2 font-extrabold mb-2">{companyValues[activeValue].stat}</div>
                  <div className="text-body text-white/80 mb-6">{companyValues[activeValue].statLabel}</div>
                  <p className="text-body text-white/90 leading-relaxed">{companyValues[activeValue].description}</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Journey Section */}
      <section id="our-journey" className="py-16 md:py-24 bg-gradient-to-b from-purple-50/20 via-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="badge badge-purple inline-flex mb-4">
              <FontAwesomeIcon icon="calendar-check" />
              <span>Development Timeline</span>
            </div>
            
            <h2 className="text-h2 mb-6 text-dark">
              From Idea to <span className="text-gradient-blue-pink">Reality</span>
            </h2>
            <p className="text-body-lg text-gray-600">Six months of dedicated development to bring you the perfect solution</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'lightbulb', title: 'The Beginning', desc: 'March 2025 - Identified the need for sustainable digital networking', gradient: 'from-blue-400 to-blue-600' },
                { icon: 'code', title: 'Development', desc: 'May 2025 - Built complete platform with all features in-house', gradient: 'from-purple-400 to-purple-600' },
                { icon: 'rocket', title: 'Launch Ready', desc: 'July 2025 - Preparing to revolutionize networking worldwide', gradient: 'from-green-400 to-green-600' }
              ].map((item, index) => (
                <div key={index} className="text-center group">
                  <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center text-white text-2xl mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <h3 className="text-h5 text-dark mb-2">{item.title}</h3>
                  <p className="text-body-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section 1 */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 mb-8">
              <span className="text-white">Ready to </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Transform Your Life?</span>
            </h2>
            <button onClick={() => setShowComingSoonPopup(true)} className="btn inline-flex items-center gap-3 px-8 py-4 text-btn-lg font-semibold bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
              <FontAwesomeIcon icon="download" />
              <span>Download the App</span>
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img src="/assets/image8.jpg" alt="Platform Excellence" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                    <div className="grid grid-cols-3 gap-4 text-white text-center">
                      {['Fast', 'Secure', 'Smart'].map((item, i) => (
                        <div key={i}>
                          <div className="text-h4">{item}</div>
                          <div className="text-body-sm opacity-80">{['Performance', 'Platform', 'Features'][i]}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="badge badge-green inline-flex mb-6">
                  <FontAwesomeIcon icon="award" />
                  <span>Excellence</span>
                </div>
                
                <h2 className="text-h2 mb-6 text-dark">
                  Why Choose <span className="text-gradient-blue-pink">AM CARDMATE</span>
                </h2>
                
                <p className="text-body-lg text-gray-600 mb-8 leading-relaxed">
                  We've built every feature with obsessive attention to detail, ensuring a seamless 
                  experience that delights users and delivers real business value.
                </p>
                
                <div className="space-y-6">
                  {[
                    { title: 'All-in-One Platform', desc: 'No need for multiple tools. Everything from creation to analytics in one place.' },
                    { title: 'Instant Updates', desc: 'Change your info once, and it updates everywhere instantly. No reprinting.' },
                    { title: 'Professional Growth', desc: 'Track your networking effectiveness and grow your professional reach.' },
                    { title: 'Sustainable Choice', desc: 'Join the paperless revolution and reduce your environmental footprint.' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FontAwesomeIcon icon="check" className="text-green-600 text-sm" />
                      </div>
                      <div>
                        <h4 className="text-body font-bold text-dark mb-1">{item.title}</h4>
                        <p className="text-body-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Platform Features */}
      <section id="platform-features" className="py-16 md:py-20 bg-gradient-to-b from-purple-50/20 via-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="cube" />
              <span>Complete Platform</span>
            </div>
            
            <h2 className="text-h2 mb-6">
              Everything Built <span className="text-gradient-blue-pink">In-House</span>
            </h2>
            <p className="text-body-lg text-gray-600">No external dependencies. No third-party tools. Just pure innovation.</p>
          </div>
          
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <motion.div key={index} className="h-full bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300" whileHover={{ y: -10 }}>
                <motion.div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center text-white text-xl mb-4`} whileHover={{ scale: 1.1, rotate: 360 }} transition={{ duration: 0.6 }}>
                  <FontAwesomeIcon icon={feature.icon} />
                </motion.div>
                <h3 className="text-h5 text-dark mb-2">{feature.title}</h3>
                <p className="text-h4 text-gradient-blue-pink mb-3">{feature.stat}</p>
                <p className="text-body-sm text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="badge badge-green inline-flex mb-4">
              <FontAwesomeIcon icon="comments" />
              <span>What People Say</span>
            </div>
            
            <h2 className="text-h2 mb-6 text-dark">
              Trusted by <span className="text-gradient-blue-pink">Professionals</span>
            </h2>
          </div>
          
          <div className="relative">
            <div className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-auto pb-4 sm:pb-6 pt-2 px-2 sm:px-4 snap-x snap-mandatory scrollbar-hide scroll-smooth -mx-2 sm:-mx-4">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 sm:p-8 md:p-10 min-w-[280px] sm:min-w-[320px] md:min-w-[400px] lg:min-w-[450px] max-w-[320px] sm:max-w-[400px] md:max-w-[480px] flex-none snap-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex flex-col items-center text-center gap-4 sm:gap-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white text-body-lg font-bold shadow-lg">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-center gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FontAwesomeIcon key={i} icon="star" className="text-yellow-400 text-body-sm" />
                        ))}
                      </div>
                      <blockquote className="text-body text-gray-600 italic mb-4 sm:mb-6 leading-relaxed">"{testimonial.quote}"</blockquote>
                      <div>
                        <p className="text-body font-bold text-dark">{testimonial.name}</p>
                        <p className="text-body-sm text-gray-600">{testimonial.role}</p>
                        <p className="text-caption text-blue-600 font-medium">{testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex sm:hidden justify-center mt-4">
              <p className="text-caption text-gray-500 flex items-center gap-2">
                <FontAwesomeIcon icon="hand-pointer" />
                <span>Swipe for more</span>
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section 2 */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 mb-8">
              <span className="text-white">Choose Your </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Perfect Plan</span>
            </h2>
            <button onClick={() => setShowComingSoonPopup(true)} className="btn inline-flex items-center gap-3 px-8 py-4 text-btn-lg font-semibold bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
              <FontAwesomeIcon icon="download" />
              <span>Download the App</span>
            </button>
          </div>
        </div>
      </section>
      
      {/* Founder Section */}
      <section id="our-team" className="py-16 md:py-24 bg-gradient-to-b from-purple-50/20 via-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="user" />
              <span>Meet the Founder</span>
            </div>
            
            <h2 className="text-h2 mb-6 text-dark">
              The Mind Behind <span className="text-gradient-blue-pink">AM CARDMATE</span>
            </h2>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                  <div className="space-y-6">
                    {[
                      { stat: '6 months', label: 'From idea to launch' },
                      { stat: 'Full-stack', label: 'Developer & Designer' },
                      { stat: '1 vision', label: 'Sustainable networking' }
                    ].map((item, index) => (
                      <div key={index} className="text-center lg:text-left">
                        <div className="text-h3 text-gradient-blue-pink mb-1">{item.stat}</div>
                        <div className="text-body text-gray-600">{item.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="lg:col-span-2">
                    <blockquote className="relative">
                      <FontAwesomeIcon icon="quote-left" className="text-4xl text-primary-blue/20 absolute -top-2 left-0" />
                      <p className="text-body-lg text-gray-600 italic leading-relaxed mb-6 pl-12">
                        "My goal is simple: to make professional networking as seamless and insightful as 
                        it should be in the digital age. Every feature in AM CARDMATE is designed with 
                        real professionals in mind."
                      </p>
                      <cite className="block text-primary-blue font-semibold pl-12">- Alina Macovei, Founder & CEO</cite>
                    </blockquote>
                    
                    <div className="mt-8 p-6 bg-gradient-to-r from-primary-blue/5 to-accent-purple/5 rounded-2xl">
                      <p className="text-body text-gray-600 leading-relaxed">
                        A graduate with a Computer Science degree from Lancaster University, my passion for digital 
                        experiences fuels my journey as a Developer & Digital Designer.
                      </p>
                      
                      <div className="flex gap-4 mt-6 justify-center lg:justify-start">
                        <motion.a href="https://www.linkedin.com/in/alina-macovei/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300" whileHover={{ scale: 1.1, rotate: 360 }} transition={{ duration: 0.3 }}>
                          <FontAwesomeIcon icon={['fab', 'linkedin-in']} />
                        </motion.a>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {[
                    { icon: 'code', label: 'Full-Stack Developer', gradient: 'from-primary-blue/10 to-primary-blue/5' },
                    { icon: 'paint-brush', label: 'Digital Designer', gradient: 'from-accent-purple/10 to-accent-purple/5' },
                    { icon: 'lightbulb', label: 'Innovation Leader', gradient: 'from-primary-pink/10 to-primary-pink/5' }
                  ].map((item, index) => (
                    <motion.div key={index} className={`bg-gradient-to-r ${item.gradient} rounded-xl p-4 text-center`} whileHover={{ scale: 1.05 }}>
                      <FontAwesomeIcon icon={item.icon} className={`text-2xl mb-2 ${index === 0 ? 'text-primary-blue' : index === 1 ? 'text-accent-purple' : 'text-primary-pink'}`} />
                      <div className="text-body-sm font-medium text-gray-600">{item.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    
      {/* Demo Popup */}
      <AnimatePresence>
        {showDemoPopup && (
          <motion.div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowDemoPopup(false)}>
            <motion.div className="bg-white rounded-2xl p-8 max-w-md w-full text-center" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
              <motion.div className="w-20 h-20 bg-gradient-to-br from-primary-blue to-accent-purple rounded-full flex items-center justify-center mx-auto mb-6" animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                <FontAwesomeIcon icon="play-circle" className="text-white text-3xl" />
              </motion.div>
              <h3 className="text-h4 text-dark mb-4">Demo Videos Coming Soon!</h3>
              <p className="text-body text-gray-600 mb-6">We're working on creating amazing demo videos to show you exactly how AM CARDMATE works.</p>
              <motion.button className="btn btn-primary w-full" onClick={() => setShowDemoPopup(false)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>Got it!</motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Download Popup */}
      {showComingSoonPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowComingSoonPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="rocket" className="text-white text-2xl sm:text-3xl" />
              </div>
              <h3 className="text-h4 text-gray-900 mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray-600 mb-8">AM CARDMATE is now available on the App Store and Google Play!</p>
              <div className="grid grid-cols-1 gap-3 mb-6">
                <button onClick={() => window.open('https://apps.apple.com/pt/app/am-cardmate/id6747951046', '_blank')} className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all">
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">Download on the</div>
                    <div className="text-body font-bold">App Store</div>
                  </div>
                </button>
                <button onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amcardmate.app', '_blank')} className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all">
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">GET IT ON</div>
                    <div className="text-body font-bold">Google Play</div>
                  </div>
                </button>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-btn font-semibold hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => setShowComingSoonPopup(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;